import * as Dialog from '@radix-ui/react-dialog';
import { forwardRef, type MouseEventHandler, type ReactNode, useRef } from 'react';

import { type CloseButtonColorScheme } from './ModalContent';
import { useModalContext } from './ModalContext';

import { useForwardedRef } from '../../hooks/useForwardedRef';
import { useIsMobileScreenSize } from '../../hooks/useIsMobileScreenSize';
import { IconButton } from '../../server/IconButton/IconButton';
import { NavigationButton } from '../../server/NavigationButton/NavigationButton';
import { classNames } from '../../server/utils/classNames';

export type FullPageModalContentProps = {
	children: React.ReactNode;
	className?: string;
	backgroundClassName?: string;
	contentTop?: ReactNode;
	onBackButtonClick?: MouseEventHandler<HTMLButtonElement>;
	closeButtonColorScheme?: CloseButtonColorScheme;
};

const FullPageModalHeader = ({
	onBackButtonClick,
	closeButtonColorScheme = 'default',
}: {
	onBackButtonClick: FullPageModalContentProps['onBackButtonClick'];
	closeButtonColorScheme: FullPageModalContentProps['closeButtonColorScheme'];
}) => {
	const { analyticsId } = useModalContext();

	return (
		<header
			className={classNames('pt-5 px-2 max-w-5xl w-full flex justify-end', {
				'justify-between': onBackButtonClick,
			})}>
			{onBackButtonClick && <NavigationButton analyticsId={analyticsId} variant="back" onClick={onBackButtonClick} />}
			<Dialog.Close asChild>
				<IconButton
					analyticsId={null}
					data-testid="full-page-modal-close-button"
					icon="close"
					ariaLabel="Close"
					className={classNames('text-content-primary', {
						dark: closeButtonColorScheme === 'light',
						light: closeButtonColorScheme === 'dark',
					})}
				/>
			</Dialog.Close>
		</header>
	);
};

export const FullPageModalContent = forwardRef<HTMLDivElement, FullPageModalContentProps>(
	({ contentTop, onBackButtonClick, closeButtonColorScheme = 'default', ...props }, ref) => {
		const isMobileScreenSize = useIsMobileScreenSize();
		const overlayRef = useRef<HTMLDivElement | null>(null);
		const forwardedContentRef = useForwardedRef(ref);

		return (
			<Dialog.Portal>
				<Dialog.Overlay
					className={classNames(
						'fixed inset-0 flex flex-col group/modal z-[10000]',
						'w-screen grow focus:outline-none bg-transparent',
						'data-[state=closed]:animate-full-page-modal-slide-down',
						// the order of these is important, because the `h-screen` is a default and h-dvh will only apply if the browser supports dvh units
						'h-screen supports-[height:100dvh]:h-dvh',
					)}>
					{contentTop}
					<div
						onAnimationStart={() => {
							const currentContent = forwardedContentRef.current;
							const currentOverlay = overlayRef.current;

							if (currentContent?.clientHeight && currentOverlay?.clientHeight) {
								const isContentScrollable = currentContent?.clientHeight > currentOverlay.clientHeight;

								if (isContentScrollable) {
									currentOverlay.style.overflowY = 'auto';
								} else {
									currentOverlay.style.overflowY = 'hidden';
								}
							}
						}}
						ref={overlayRef}
						className={classNames(
							'w-screen grow bg-shade-primary-600 flex flex-col',
							'animate-full-page-modal-overlay-fade-in',
							'group-data-[state=closed]/modal:animate-full-page-modal-overlay-fade-out',
						)}>
						<Dialog.Content
							onAnimationEnd={() => {
								if (overlayRef.current) {
									overlayRef.current.style.overflowY = 'auto';
								}
							}}
							onPointerDownOutside={event => {
								event.preventDefault();
							}}
							ref={forwardedContentRef}
							className={classNames(
								'flex flex-col items-center h-full sm:h-max w-full grow bg-bg-primary',
								'animate-full-page-modal-content-fade-in',
								'data-[state=closed]:animate-full-page-modal-content-fade-out',
								'data-[state=open]:opacity-0',
							)}
							// aria-describedby={undefined} is required for Radix Dialog to indicate that no description is provided.
							aria-describedby={undefined}>
							<div className={classNames('w-full h-full flex-col flex items-center', props.backgroundClassName)}>
								{!isMobileScreenSize && (
									<FullPageModalHeader
										onBackButtonClick={onBackButtonClick}
										closeButtonColorScheme={closeButtonColorScheme}
									/>
								)}
								<div
									className={classNames('w-full sm:max-w-layout-compact h-full grow flex flex-col', props.className)}>
									{isMobileScreenSize && (
										<FullPageModalHeader
											onBackButtonClick={onBackButtonClick}
											closeButtonColorScheme={closeButtonColorScheme}
										/>
									)}
									{props.children}
								</div>
							</div>
						</Dialog.Content>
					</div>
				</Dialog.Overlay>
			</Dialog.Portal>
		);
	},
);
